import {
  Alert,
  FilterSearchAndSelect,
  FilterToggle,
  Table,
  type TableProps,
} from "@pharmupp/p3-front-commons"
import type { ReactNode } from "react"
import {
  type OfferTagValue,
  type TagObject,
  openInvoice,
  tagObjectRecord,
} from "../../../../Offers"
import type { ApiOrder } from "../../types"
import type {
  Laboratory,
  OffersOrderReferential,
  useAdherentOrdersReferential,
} from "../../useAdherentOrdersReferential"

type OrderTableProps = {
  displayCancelled?: boolean
  displayStatusFilter?: boolean
  showDateYear?: boolean
  alertText?: ReactNode
  extraFilterElements?: ReactNode
  onRowClick?: (row: OrderRow) => void
  rowClickCondition?: (row: OrderRow) => boolean
  renderRowTooltip?: (row: OrderRow) => ReactNode
} & ReturnType<typeof useAdherentOrdersReferential> &
  Pick<
    TableProps<ApiOrder, OrderRow>,
    "queryConfig" | "emptyMessage" | "exportButtonProps"
  > &
  Required<Pick<TableProps<ApiOrder, OrderRow>, "columns">>

export const OrderTable = ({
  queryConfig,
  referential,
  displayCancelled = true,
  displayStatusFilter = true,
  showDateYear = false,
  alertText,
  extraFilterElements,
  ...props
}: OrderTableProps) => (
  <Table<ApiOrder, OrderRow>
    queryConfig={{
      ...queryConfig,
      select: getOrdersSelector({ displayCancelled, showDateYear, referential }),
    }}
    renderAlerts={() =>
      !!alertText && (
        <Alert
          severity="info"
          variant="standard"
          sx={{ borderRadius: "0px", px: 4 }}
        >
          <i>{alertText}</i>
        </Alert>
      )
    }
    FilterSectionElement={
      !!referential && (
        <>
          <FilterToggle name="onlyChallenge" label="Challenge" />
          <FilterSearchAndSelect
            name="offerSource"
            label="Canal"
            options={referential.sources}
          />
          <FilterSearchAndSelect
            name="tag"
            label="Catégorie"
            options={referential.tags}
          />
          <FilterSearchAndSelect
            name="labId"
            label="Laboratoires"
            options={referential.laboratories}
          />
          {extraFilterElements}
          {!!displayStatusFilter && (
            <FilterSearchAndSelect
              name="status"
              label="Statut"
              options={referential.status}
            />
          )}
        </>
      )
    }
    onRowClick={(row) => openInvoice(row.viewLink)}
    rowClickCondition={(row) => !!row.viewLink}
    renderRowTooltip={(row) => {
      if (!row.viewLink) {
        return <p>! Commande indisponible</p>
      }
    }}
    {...props}
  />
)

export type OrderRow = ApiOrder & {
  displayCancelled: boolean
  showDateYear: boolean
  offerTag?: TagObject
  offerSource: string
  laboratory: Laboratory | null
}

const getOfferTag = (
  tags: OfferTagValue[] | null | undefined,
): TagObject | undefined => {
  const hasTags = tags?.length
  const hasChallengeTag =
    hasTags && tags?.includes("ORDER_CHALLENGE" satisfies OfferTagValue)
  let offerTag: TagObject | undefined
  if (hasChallengeTag) {
    offerTag = tagObjectRecord.ORDER_CHALLENGE
  } else if (hasTags) {
    offerTag = tagObjectRecord[tags[0]]
  }

  return offerTag
}

export const getOrdersSelector =
  ({
    displayCancelled = true,
    showDateYear = false,
    referential,
  }: {
    displayCancelled?: boolean
    showDateYear?: boolean
    referential: OffersOrderReferential
  }) =>
  (orders: ApiOrder[]): OrderRow[] =>
    orders.map((order) => ({
      ...order,
      displayCancelled,
      showDateYear,
      offerTag: getOfferTag(order.offer.tags),
      offerSource:
        referential?.sourcesRecord[order.offer.source] ?? order.offer.source,
      laboratory:
        order.laboratoryId && referential
          ? referential.laboratoriesRecord[order.laboratoryId]
          : null,
    }))
